import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BACKEND_API_URL } from './backend-api.tokens';
import { ORGANIZATION_SITE_CODE, WRONG_SITE_PATH, WRONG_SITE_PENDING_KEY, organizationSiteUrl } from './organization-site';
import { SpaqFireDb } from './spaq-fire-db.service';

/**
 * Sends the user to the wrong-site page when `enforceOrganizationSite` returns 403.
 * The account is signed out first: this site is not theirs.
 * Other 403s are left alone: the body has to carry `code: organization_site`.
 */
@Injectable()
export class OrganizationSiteInterceptor implements HttpInterceptor {
	private leaving = false;

	constructor(
		@Inject(BACKEND_API_URL) private backendApiUrl: string,
		private afAuth: AngularFireAuth,
		private db: SpaqFireDb
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (!req.url.startsWith(this.backendApiUrl)) {
			return next.handle(req);
		}

		return next.handle(req).pipe(
			catchError(error => {
				if (this.isWrongSite(error) && !this.leaving && !window.location.pathname.startsWith(WRONG_SITE_PATH)) {
					this.leaveForOrganizationSite(error);
				}
				return throwError(error);
			})
		);
	}

	private leaveForOrganizationSite(error: HttpErrorResponse): void {
		this.leaving = true;
		const site = organizationSiteUrl(error.error?.domain, window.location.hostname);
		const query = site ? `?site=${encodeURIComponent(site)}` : '';
		sessionStorage.setItem(WRONG_SITE_PENDING_KEY, '1');
		this.db.goOffline();
		this.afAuth
			.signOut()
			.catch(() => undefined)
			.finally(() => {
				window.location.replace(`${WRONG_SITE_PATH}${query}`);
			});
	}

	private isWrongSite(error: unknown): error is HttpErrorResponse {
		return error instanceof HttpErrorResponse && error.status === 403 && error.error?.code === ORGANIZATION_SITE_CODE;
	}
}
